import type { ICameraVideoTrack, ILocalTrack } from 'agora-rtc-react';
import AgoraRTC, { IAgoraRTCClient } from 'agora-rtc-react';
import { StudioLogger } from '@studio/logger';
import { setupVideoDeviceListeners } from '../events/setupDeviceListeners';
import { AGORA_ENCODER_CONFIG } from '@studio/constants';

const LOG = new StudioLogger('agoraVideoControl');

interface TransformableCameraTrack {
  transformedVideoTrack?: ICameraVideoTrack | null;
}

/* eslint-disable no-unused-vars */
interface Actions {
  showNotification: (...args: any[]) => any;
  handleVideoInputChanged: (...args: any[]) => any;
  handleAudioInputChanged: (...args: any[]) => any;
  addToAttendeeTiles: (...args: any[]) => any;
}

/**
 * Toggles video for an Agora attendee by enabling/disabling the video track
 * @param videoTrack - The video track to toggle
 * @param enabled - Whether to enable (true) or disable (false) the video
 * @returns {Promise<ICameraVideoTrack | null>} - The video track if enabled, null if disabled or track not provided
 */
export const toggleAgoraVideo = async (
  videoTrack: ICameraVideoTrack | null,
  enabled: boolean,
  client: IAgoraRTCClient,
  dispatch: (action: any) => void = () => {},
  deviceActions: Actions = {} as Actions,
  attendeeId = ''
): Promise<ICameraVideoTrack | null> => {
  try {
    LOG.info(`${enabled ? 'Enabling' : 'Disabling'} Agora video`, { enabled });

    if (!videoTrack) {
      try {
        const cameras = await AgoraRTC.getCameras();
        if (cameras.length === 0) throw new Error('No camera found');
        const cameraId = cameras[0]?.deviceId;
        const newVideoTrack: ICameraVideoTrack = await AgoraRTC.createCameraVideoTrack({
          cameraId,
          encoderConfig: AGORA_ENCODER_CONFIG
        });

        setupVideoDeviceListeners(newVideoTrack, dispatch, deviceActions, attendeeId);
        await client.publish(newVideoTrack);
        LOG.info(`Successfully created new video track for device: ${cameraId}`);
        return newVideoTrack;
      } catch (error) {
        LOG.error('Failed to create new video track when toggling video:', error);
        return null;
      }
    }

    const trackToToggle: ICameraVideoTrack | null =
      (videoTrack as TransformableCameraTrack)?.transformedVideoTrack ?? videoTrack;

    if (trackToToggle) {
      await trackToToggle.setEnabled(enabled);
      LOG.info(`Agora video track ${enabled ? 'enabled' : 'disabled'}`);
      return enabled ? trackToToggle : null;
    } else {
      LOG.info('No video track provided');
      return null;
    }
  } catch (error) {
    LOG.error(`Failed to ${enabled ? 'enable' : 'disable'} Agora video`, error);
    throw error;
  }
};

/**
 * Switches the video input device for the local video track
 * @param client - The Agora RTC client instance
 * @param deviceId - The new video input device ID
 * @param transformedTrack - Optional transformed track to reuse instead of creating a raw track
 * @returns Promise<ICameraVideoTrack | null> - The active video track or null if creation fails
 */
export async function switchVideoInput(
  client: IAgoraRTCClient,
  deviceId: string,
  currentVideoTrack: ICameraVideoTrack | null = null,
  transformedTrack: ICameraVideoTrack | null = null,
  dispatch: (action: any) => void = () => {},
  deviceActions: Actions = {} as Actions,
  attendeeId = ''
): Promise<ICameraVideoTrack | null> {
  try {
    if (!client) {
      throw new Error('Agora RTC client is not initialized.');
    }

    if (!currentVideoTrack) {
      try {
        const newVideoTrack: ICameraVideoTrack = await AgoraRTC.createCameraVideoTrack({
          cameraId: deviceId,
          encoderConfig: AGORA_ENCODER_CONFIG
        });

        setupVideoDeviceListeners(newVideoTrack, dispatch, deviceActions, attendeeId);
        await client.publish(newVideoTrack);
        LOG.info(`Successfully created new video track for device: ${deviceId}`);
        return newVideoTrack;
      } catch (error) {
        LOG.error('Failed to create new video track when switching video input:', error);
        return null;
      }
    }

    if (transformedTrack) {
      await client.unpublish(currentVideoTrack);
      await client.publish(transformedTrack);
      LOG.info(`Published transformed video track for device: ${deviceId}`);
      return transformedTrack;
    }
    await currentVideoTrack.setDevice(deviceId);
    LOG.info(`Successfully switched video input device to: ${deviceId}`);
    return currentVideoTrack;
  } catch (error) {
    LOG.error(`Failed to switch video input device to ${deviceId}:`, error);
    throw error;
  }
}

/**
 * Stops a video input track by unpublishing, stopping, and closing it
 * @param client - The Agora RTC client instance
 * @param videoTrack - The video track to stop
 * @param deviceId - Optional device identifier for logging
 */
export async function stopVideoInput(
  client: IAgoraRTCClient,
  videoTrack: ICameraVideoTrack | ILocalTrack | null,
  deviceId?: string
): Promise<void> {
  try {
    if (!client) {
      throw new Error('Agora RTC client is not initialized.');
    }

    if (!videoTrack) {
      LOG.info('No video track provided to stop');
      return;
    }

    if (client.localTracks?.includes(videoTrack)) {
      await client.unpublish(videoTrack);
      LOG.info(`Unpublished video track before stopping for ${deviceId}`);
    }

    videoTrack.stop();
    videoTrack.close();
    LOG.info(`Stopped and closed video track for ${deviceId}`);
  } catch (error) {
    LOG.error(`Failed to stop video input for ${deviceId}:`, error);
    throw error;
  }
}

/**
 * Lists all available video input devices
 * @returns Promise<MediaDeviceInfo[]> - Array of available video input devices
 */
export async function listVideoInputDevices(): Promise<MediaDeviceInfo[]> {
  try {
    const devices = await AgoraRTC.getDevices();
    const videoInputDevices = devices.filter(device => device.kind === 'videoinput');

    return videoInputDevices;
  } catch (error) {
    LOG.error('Failed to list video input devices:', error);
    throw error;
  }
}
